import { useRef, useState } from 'react';
import { Field } from '@/isomorphic/components/forms/components/field';
import { Button } from '@/isomorphic/components/typography/button/button';
import { validators } from '@/hooks/use-validation';
import { GoogleRecaptchaInfo } from '@/isomorphic/components/google-recaptcha-info/google-recaptcha-info';
import { CompanyCreditCheckLabels, ContentAlign } from './company-credit-check';
import { CompanyCreditCheckFields } from './use-company-credit-check';

const DEBUG_BYPASS_ORG_NUMBERS = ['5164010208'];

const TEXT_ALIGN: Record<ContentAlign, string> = {
    left: 'r-text-left',
    center: 'r-text-center',
};

const BUTTON_ALIGN: Record<ContentAlign, string> = {
    left: '!r-mr-auto',
    center: '!r-mx-auto',
};

type StepOneProps = {
    labels: CompanyCreditCheckLabels;
    align?: ContentAlign;
    fields: CompanyCreditCheckFields;
    updateField: (updates: Partial<CompanyCreditCheckFields>) => void;
    onSubmit: () => void;
    minLoanAmount: number;
    captchaError?: boolean;
    enableRecaptcha?: boolean;
    debugMode?: boolean;
};

const formatAmount = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';
    return Number(digits).toLocaleString('sv-SE');
};

const parseAmount = (value: string): string => {
    return value.replace(/\D/g, '');
};

export const StepOne = ({
    labels,
    align = 'left',
    fields,
    updateField,
    onSubmit,
    minLoanAmount,
    captchaError,
    enableRecaptcha = true,
    debugMode = false,
}: StepOneProps) => {
    const textAlign = TEXT_ALIGN[align];
    const buttonAlign = BUTTON_ALIGN[align];
    const containerRef = useRef<HTMLDivElement>(null);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);

    const normalizedOrg = fields.orgNumber.replace(/\D/g, '');
    const isOrgNumberValid =
        (debugMode && DEBUG_BYPASS_ORG_NUMBERS.includes(normalizedOrg)) ||
        validators.companyRegistrationNumber(fields.orgNumber, 'sv', { sv: { strict: true } });
    const parsedAmount = Number(parseAmount(fields.loanAmount));
    const isAmountValid = parsedAmount >= minLoanAmount;
    const isAmountEntered = fields.loanAmount.length > 0;

    const scrollToAndFocusFirstError = () => {
        setTimeout(() => {
            if (containerRef.current) {
                const firstError = containerRef.current.querySelector<HTMLElement>('[aria-invalid="true"]');
                if (firstError) {
                    firstError.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    firstError.focus();
                }
            }
        }, 100);
    };

    const handleSubmit = () => {
        setIsFormSubmitted(true);

        if (!isOrgNumberValid || !isAmountValid) {
            scrollToAndFocusFirstError();
            return;
        }

        onSubmit();
    };

    const getAmountErrorMessage = (): string => {
        if (!isAmountEntered) {
            return labels.loanAmountErrorText;
        }
        if (!isAmountValid) {
            return labels.loanAmountMinErrorText;
        }
        return '';
    };

    return (
        <div ref={containerRef} className="r-flex r-flex-col r-gap-6">
            <div className={`r-flex r-flex-col r-gap-2 ${textAlign}`}>
                <h2 className="r-text-2xl r-font-medium r-text-gray-900">{labels.stepOneTitle}</h2>
                <p className="r-text-base r-text-gray-500">{labels.stepOneDescription}</p>
            </div>

            <p className={`r-text-base r-text-gray-700 ${textAlign}`}>{labels.stepOneInstruction}</p>

            <div className="r-flex r-flex-col r-gap-4">
                <Field
                    fieldName="orgNumber"
                    label={labels.orgNumberLabel}
                    value={fields.orgNumber}
                    onChange={value => updateField({ orgNumber: value })}
                    validation={value =>
                        (debugMode && DEBUG_BYPASS_ORG_NUMBERS.includes(value.replace(/\D/g, ''))) ||
                        validators.companyRegistrationNumber(value, 'sv', { sv: { strict: true } })
                    }
                    errorMessage={labels.orgNumberErrorText}
                    required
                    showRequiredError={isFormSubmitted && !isOrgNumberValid}
                />

                <Field
                    fieldName="loanAmount"
                    label={labels.loanAmountLabel}
                    value={formatAmount(fields.loanAmount)}
                    onChange={value => updateField({ loanAmount: parseAmount(value) })}
                    inputMode="numeric"
                    validation={value => Number(parseAmount(value)) >= minLoanAmount}
                    errorMessage={getAmountErrorMessage()}
                    required
                    showRequiredError={isFormSubmitted && !isAmountValid}
                />
            </div>

            {captchaError && (
                <div role="alert" className="r-rounded-xl r-bg-red-100 r-p-4 r-text-sm r-text-red-700">
                    {labels.captchaErrorText}
                </div>
            )}

            <Button type="primary" className={`${buttonAlign} r-w-fit`} onClick={handleSubmit}>
                {labels.submitButtonText}
            </Button>

            {labels.disclaimer && <p className={`r-text-sm r-text-gray-500 ${textAlign}`}>{labels.disclaimer}</p>}

            {enableRecaptcha && (
                <div className={textAlign}>
                    <GoogleRecaptchaInfo />
                </div>
            )}
        </div>
    );
};
